import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

type ResponsiveImageProps = {
  base: string;
  widths: number[];
  sizes: string;
  alt: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  imgSx?: SxProps<Theme>;
};

function srcSet(base: string, widths: number[], ext: 'avif' | 'webp') {
  return widths.map((width) => `/images/opt/${base}-${width}.${ext} ${width}w`).join(', ');
}

/** AVIF, then WebP, with width variants so phones don't download the desktop file. */
const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  base,
  widths,
  sizes,
  alt,
  loading = 'lazy',
  fetchPriority = 'auto',
  imgSx,
}) => {
  const fallback = `/images/opt/${base}-${widths[widths.length - 1]}.webp`;

  return (
    <Box component="picture" sx={{ display: 'block', width: '100%', height: '100%' }}>
      <source type="image/avif" srcSet={srcSet(base, widths, 'avif')} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet(base, widths, 'webp')} sizes={sizes} />
      <Box
        component="img"
        src={fallback}
        srcSet={srcSet(base, widths, 'webp')}
        sizes={sizes}
        alt={alt}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={loading === 'lazy' ? 'async' : 'auto'}
        sx={[{ width: '100%', height: '100%', display: 'block' }, imgSx] as SxProps<Theme>}
      />
    </Box>
  );
};

export default ResponsiveImage;
